import React, { useState, useEffect } from "react";
import { FaMicrophone, FaMapMarkerAlt, FaPhone, FaEnvelope, FaClock, FaWifi, FaParking, FaCoffee, FaImage, FaTimes, FaUpload, FaCheckCircle } from "react-icons/fa";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import stateCityData from "../../../data/states.json";

const PodcastStudioRegister = () => {
  const [formData, setFormData] = useState({
    studioName: "",
    location: "",
    address: "",
    city: "",
    state: "",
    pincode: "",
    contactPerson: "",
    phone: "",
    email: "",
    capacity: "",
    hourlyRate: "",
    facilities: [],
    availability: {
      monday: { start: "", end: "", available: false },
      tuesday: { start: "", end: "", available: false },
      wednesday: { start: "", end: "", available: false },
      thursday: { start: "", end: "", available: false },
      friday: { start: "", end: "", available: false },
      saturday: { start: "", end: "", available: false },
      sunday: { start: "", end: "", available: false }
    },
    description: "",
    images: []
  });

  const [states] = useState(Object.keys(stateCityData));
  const [cities, setCities] = useState([]);
  const [selectedImages, setSelectedImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [submissionStatus, setSubmissionStatus] = useState(null);
  const [canSubmit, setCanSubmit] = useState(true);
  const [validationErrors, setValidationErrors] = useState({});
  const [confirmDetails, setConfirmDetails] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  // Check submission status on component mount
  useEffect(() => {
    const checkSubmissionStatus = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/podcast/studio/submission-status`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          }
        });

        const data = await response.json();

        if (data.success) {
          setSubmissionStatus(data.data);
          setCanSubmit(data.data.canSubmit);

          // Show rejection alert if studio was rejected
          if (data.data.hasStudio && data.data.studio?.status === 'rejected') {
            const rejectionReason = data.data.studio.rejectionReason || 'No specific reason provided';
            const adminNotes = data.data.studio.adminNotes || '';

            // Show detailed rejection alert
            const alertMessage = adminNotes
              ? `Your studio registration was rejected.\n\nReason: ${rejectionReason}\n\nAdmin Notes: ${adminNotes}\n\nYou can submit a new registration with the required changes.`
              : `Your studio registration was rejected.\n\nReason: ${rejectionReason}\n\nYou can submit a new registration with the required changes.`;

            // Use SweetAlert2 for better alert display
            import('sweetalert2').then(({ default: Swal }) => {
              Swal.fire({
                title: 'Studio Registration Rejected',
                html: `
                  <div class="text-left">
                    <div class="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                      <div class="flex items-start">
                        <div class="flex-shrink-0">
                          <svg class="h-6 w-6 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                          </svg>
                        </div>
                        <div class="ml-3">
                          <h3 class="text-sm font-medium text-red-800">Rejection Details</h3>
                          <div class="mt-2 text-sm text-red-700">
                            <p><strong>Reason:</strong> ${rejectionReason}</p>
                            ${adminNotes ? `<p class="mt-2"><strong>Admin Notes:</strong> ${adminNotes}</p>` : ''}
                          </div>
                        </div>
                      </div>
                    </div>
                    <p class="text-gray-600 text-sm">You can submit a new registration with the required changes.</p>
                  </div>
                `,
                icon: 'error',
                iconColor: '#dc2626',
                confirmButtonText: 'Submit New Registration',
                confirmButtonColor: '#dc2626',
                showCancelButton: true,
                cancelButtonText: 'View Details',
                cancelButtonColor: '#6b7280',
                width: '500px',
                padding: '2rem',
                customClass: {
                  popup: 'rounded-2xl shadow-2xl',
                  title: 'text-xl font-bold text-gray-900 mb-4',
                  confirmButton: 'px-6 py-3 rounded-lg font-semibold text-sm',
                  cancelButton: 'px-6 py-3 rounded-lg font-semibold text-sm'
                }
              }).then((result) => {
                if (result.isConfirmed) {
                  // User wants to submit new registration
                  setCanSubmit(true);
                } else if (result.dismiss === Swal.DismissReason.cancel) {
                  // User wants to view details - scroll to status section
                  const statusElement = document.querySelector('.submission-status');
                  if (statusElement) {
                    statusElement.scrollIntoView({ behavior: 'smooth' });
                  }
                }
              });
            });
          }
        }
      } catch (error) {
        console.error("Error checking submission status:", error);
      }
    };

    checkSubmissionStatus();
  }, []);

  // Validation functions
  const validateField = (name, value) => {
    const errors = { ...validationErrors };

    switch (name) {
      case 'studioName':
        if (!value || !value.trim()) {
          errors[name] = 'Studio name is required';
        } else if (value.trim().length < 3) {
          errors[name] = 'Studio name must be at least 3 characters';
        } else {
          delete errors[name];
        }
        break;

      case 'location':
        if (!value || !value.trim()) {
          errors[name] = 'Location is required';
        } else if (value.trim().length < 3) {
          errors[name] = 'Location must be at least 3 characters';
        } else {
          delete errors[name];
        }
        break;

      case 'phone':
        if (!value || !value.trim()) {
          errors[name] = 'Phone number is required';
        } else if (!/^\d{10}$/.test(value.trim())) {
          errors[name] = 'Phone number must be exactly 10 digits';
        } else {
          delete errors[name];
        }
        break;

      case 'email':
        if (!value || !value.trim()) {
          errors[name] = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) {
          errors[name] = 'Please enter a valid email address';
        } else {
          delete errors[name];
        }
        break;

      case 'capacity':
        if (!value) {
          errors[name] = 'Capacity is required';
        } else if (isNaN(value) || parseInt(value) < 1 || parseInt(value) > 100) {
          errors[name] = 'Capacity must be between 1 and 100';
        } else {
          delete errors[name];
        }
        break;

      case 'hourlyRate':
        if (!value) {
          errors[name] = 'Hourly rate is required';
        } else if (isNaN(value) || parseFloat(value) < 0) {
          errors[name] = 'Hourly rate must be a positive number';
        } else {
          delete errors[name];
        }
        break;

      case 'pincode':
        if (!value || !value.trim()) {
          errors[name] = 'Pincode is required';
        } else if (!/^\d{6}$/.test(value.trim())) {
          errors[name] = 'Pincode must be exactly 6 digits';
        } else {
          delete errors[name];
        }
        break;

      case 'address':
        if (!value || !value.trim()) {
          errors[name] = 'Address is required';
        } else if (value.trim().length < 10) {
          errors[name] = 'Address must be at least 10 characters';
        } else {
          delete errors[name];
        }
        break;

      case 'contactPerson':
        if (!value || !value.trim()) {
          errors[name] = 'Contact person name is required';
        } else if (value.trim().length < 2) {
          errors[name] = 'Contact person name must be at least 2 characters';
        } else {
          delete errors[name];
        }
        break;

      case 'state':
        if (!value) {
          errors[name] = 'State is required';
        } else {
          delete errors[name];
        }
        break;

      case 'city':
        if (!value) {
          errors[name] = 'City is required';
        } else {
          delete errors[name];
        }
        break;

      default:
        break;
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateForm = () => {
    const requiredFields = [
      'studioName', 'location', 'address', 'city', 'state', 'pincode',
      'contactPerson', 'phone', 'email', 'capacity', 'hourlyRate'
    ];

    let isValid = true;
    const errors = {};

    requiredFields.forEach(field => {
      if (!formData[field] || formData[field].toString().trim() === '') {
        errors[field] = `${field.charAt(0).toUpperCase() + field.slice(1)} is required`;
        isValid = false;
      }
    });

    // Validate images - at least one image is required
    if (!selectedImages || selectedImages.length === 0) {
      errors.images = 'At least one studio image is required';
      isValid = false;
    }

    // Validate specific fields
    if (formData.phone && !/^\d{10}$/.test(formData.phone.trim())) {
      errors.phone = 'Phone number must be exactly 10 digits';
      isValid = false;
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      errors.email = 'Please enter a valid email address';
      isValid = false;
    }

    if (formData.pincode && !/^\d{6}$/.test(formData.pincode.trim())) {
      errors.pincode = 'Pincode must be exactly 6 digits';
      isValid = false;
    }

    if (formData.capacity && (isNaN(formData.capacity) || parseInt(formData.capacity) < 1 || parseInt(formData.capacity) > 100)) {
      errors.capacity = 'Capacity must be between 1 and 100';
      isValid = false;
    }

    if (formData.hourlyRate && (isNaN(formData.hourlyRate) || parseFloat(formData.hourlyRate) < 0)) {
      errors.hourlyRate = 'Hourly rate must be a positive number';
      isValid = false;
    }

    setValidationErrors(errors);
    return isValid;
  };

  const facilitiesOptions = [
    { id: "wifi", label: "High-Speed WiFi", icon: FaWifi },
    { id: "parking", label: "Free Parking", icon: FaParking },
    { id: "coffee", label: "Coffee/Refreshments", icon: FaCoffee },
    { id: "ac", label: "Air Conditioning" },
    { id: "soundproof", label: "Soundproof Room" },
    { id: "equipment", label: "Professional Equipment" },
    { id: "editing", label: "Editing Services" },
    { id: "storage", label: "File Storage" }
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name.startsWith("availability.")) {
      const day = name.split(".")[1];
      const field = name.split(".")[2];

      setFormData(prev => ({
        ...prev,
        availability: {
          ...prev.availability,
          [day]: {
            ...prev.availability[day],
            [field]: field === "available" ? checked : value
          }
        }
      }));
    } else if (name === "facilities") {
      setFormData(prev => ({
        ...prev,
        facilities: checked
          ? [...prev.facilities, value]
          : prev.facilities.filter(f => f !== value)
      }));
    } else {
      let processedValue = value;

      // Format phone number - remove non-digits and limit to 10 digits
      if (name === "phone") {
        processedValue = value.replace(/\D/g, '').slice(0, 10);
      }

      setFormData(prev => ({
        ...prev,
        [name]: type === "checkbox" ? checked : processedValue
      }));

      // Validate field on change
      if (type !== "checkbox") {
        validateField(name, processedValue);
      }
    }
  };

  const handleStateChange = (e) => {
    const selectedState = e.target.value;
    setFormData(prev => ({
      ...prev,
      state: selectedState,
      city: "",
    }));

    // Get cities for selected state from the imported data
    if (selectedState) {
      setCities(stateCityData[selectedState] || []);
    } else {
      setCities([]);
    }
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);

    // Validate file types
    const validFiles = files.filter(file => {
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} is not an image file`);
        return false;
      }
      return true;
    });

    // Validate file size (max 5MB per image)
    const validSizeFiles = validFiles.filter(file => {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} is too large. Maximum size is 5MB`);
        return false;
      }
      return true;
    });

    // Check total number of images (max 10)
    if (selectedImages.length + validSizeFiles.length > 10) {
      toast.error("Maximum 10 images allowed");
      return;
    }

    // Create previews
    const newPreviews = validSizeFiles.map(file => ({
      file,
      preview: URL.createObjectURL(file)
    }));

    setSelectedImages(prev => [...prev, ...validSizeFiles]);
    setImagePreviews(prev => [...prev, ...newPreviews]);
  };

  const removeImage = (index) => {
    const newImages = selectedImages.filter((_, i) => i !== index);
    const newPreviews = imagePreviews.filter((_, i) => i !== index);

    // Revoke object URL to free memory
    URL.revokeObjectURL(imagePreviews[index].preview);

    setSelectedImages(newImages);
    setImagePreviews(newPreviews);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!canSubmit) {
      toast.error("You cannot submit a new registration at this time.");
      return;
    }

    // Validate form before submission
    if (!validateForm()) {
      // Show validation errors in a floating alert
      const errorMessages = Object.values(validationErrors).join('\n');
      import('sweetalert2').then(({ default: Swal }) => {
        Swal.fire({
          title: 'Validation Errors',
          html: `
            <div class="text-left">
              <p class="text-red-600 mb-3">Please fix the following errors:</p>
              <ul class="text-sm text-red-600 space-y-1">
                ${Object.entries(validationErrors).map(([field, error]) =>
            `<li>• <strong>${field.charAt(0).toUpperCase() + field.slice(1)}:</strong> ${error}</li>`
          ).join('')}
              </ul>
            </div>
          `,
          icon: 'error',
          confirmButtonText: 'Fix Errors',
          confirmButtonColor: '#dc2626',
          width: '500px',
          position: 'top-end',
          toast: true,
          timer: 8000,
          showConfirmButton: true
        });
      });
      return;
    }

    if (!confirmDetails) {
      import('sweetalert2').then(({ default: Swal }) => {
        Swal.fire({
          title: 'Confirmation Required',
          text: 'Please confirm that all details are correct before submitting.',
          icon: 'warning',
          confirmButtonText: 'I Understand',
          confirmButtonColor: '#f59e0b',
          position: 'top-end',
          toast: true,
          timer: 5000,
          showConfirmButton: true
        });
      });
      return;
    }

    setIsLoading(true);

    try {
      // Create FormData for file upload
      const formDataToSend = new FormData();

      // Add all form fields
      Object.keys(formData).forEach(key => {
        if (key === 'availability') {
          formDataToSend.append(key, JSON.stringify(formData[key]));
        } else if (key === 'facilities') {
          formDataToSend.append(key, JSON.stringify(formData[key]));
        } else {
          formDataToSend.append(key, formData[key]);
        }
      });

      // Add images
      selectedImages.forEach((image, index) => {
        formDataToSend.append(`images`, image);
      });

      console.log("FormData contents:");
      for (let [key, value] of formDataToSend.entries()) {
        console.log(key, value);
      }
      console.log("Selected images count:", selectedImages.length);

      // Call actual API endpoint
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/podcast/studio/register`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: formDataToSend
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success(data.message || "Studio registration submitted successfully! We'll review and get back to you soon.");

        // Refresh submission status
        setCanSubmit(false);
        setSubmissionStatus({
          hasStudio: true,
          canSubmit: false,
          studio: {
            name: formData.studioName,
            status: 'pending',
            submittedAt: new Date().toISOString()
          },
          message: "Your studio registration is pending approval. Please wait for 24 hours for review."
        });

        // Reset form
        setFormData({
          studioName: "",
          location: "",
          address: "",
          city: "",
          state: "",
          pincode: "",
          contactPerson: "",
          phone: "",
          email: "",
          capacity: "",
          hourlyRate: "",
          facilities: [],
          availability: {
            monday: { start: "", end: "", available: false },
            tuesday: { start: "", end: "", available: false },
            wednesday: { start: "", end: "", available: false },
            thursday: { start: "", end: "", available: false },
            friday: { start: "", end: "", available: false },
            saturday: { start: "", end: "", available: false },
            sunday: { start: "", end: "", available: false }
          },
          description: "",
          images: []
        });

        // Reset image states
        setSelectedImages([]);
        setImagePreviews([]);
        setCities([]);
      } else {
        // Show server validation errors in a floating alert
        import('sweetalert2').then(({ default: Swal }) => {
          Swal.fire({
            title: 'Registration Failed',
            html: `
                <div class="text-left">
                  <p class="text-red-600 mb-3">${data.message || "Failed to submit registration. Please check the following:"}</p>
                  ${data.errors ? `
                    <ul class="text-sm text-red-600 space-y-1">
                      ${Object.entries(data.errors).map(([field, error]) =>
              `<li>• <strong>${field.charAt(0).toUpperCase() + field.slice(1)}:</strong> ${error.message || error}</li>`
            ).join('')}
                    </ul>
                  ` : ''}
                </div>
              `,
            icon: 'error',
            confirmButtonText: 'Fix Issues',
            confirmButtonColor: '#dc2626',
            width: '500px',
            position: 'top-end',
            toast: true,
            timer: 8000,
            showConfirmButton: true
          });
        });
      }
    } catch (error) {
      console.error("Registration error:", error);
      import('sweetalert2').then(({ default: Swal }) => {
        Swal.fire({
          title: 'Network Error',
          text: 'Failed to submit registration. Please check your internet connection and try again.',
          icon: 'error',
          confirmButtonText: 'Try Again',
          confirmButtonColor: '#dc2626',
          position: 'top-end',
          toast: true,
          timer: 5000,
          showConfirmButton: true
        });
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg p-8">
        {/* Submission Status Display */}
        {submissionStatus && !canSubmit && (
          <div className="submission-status mb-6 p-6 bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-xl shadow-lg">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                {submissionStatus.studio?.status === 'pending' ? (
                  <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                    <FaClock className="text-yellow-600 text-xl" />
                  </div>
                ) : submissionStatus.studio?.status === 'rejected' ? (
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                    <FaTimes className="text-red-600 text-xl" />
                  </div>
                ) : (
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <FaCheckCircle className="text-green-600 text-xl" />
                  </div>
                )}
              </div>
              <div className="ml-4 flex-1">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {submissionStatus.studio?.status === 'pending' ? 'Registration Pending' :
                    submissionStatus.studio?.status === 'rejected' ? 'Registration Rejected' :
                      'Studio Already Registered'}
                </h3>
                <p className="text-gray-600 mb-4">{submissionStatus.message}</p>

                {submissionStatus.studio && (
                  <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div>
                        <p className="text-sm font-medium text-gray-500">Studio Name</p>
                        <p className="text-sm font-semibold text-gray-900">{submissionStatus.studio.name}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">Submitted</p>
                        <p className="text-sm font-semibold text-gray-900">
                          {new Date(submissionStatus.studio.submittedAt).toLocaleDateString()}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">Status</p>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${submissionStatus.studio.status === 'pending'
                            ? 'bg-yellow-100 text-yellow-800'
                            : submissionStatus.studio.status === 'rejected'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-green-100 text-green-800'
                          }`}>
                          {submissionStatus.studio.status.charAt(0).toUpperCase() + submissionStatus.studio.status.slice(1)}
                        </span>
                      </div>
                    </div>

                    {submissionStatus.studio.status === 'rejected' && submissionStatus.studio.rejectionReason && (
                      <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                        <div className="flex items-start">
                          <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                            </svg>
                          </div>
                          <div className="ml-3">
                            <h4 className="text-sm font-semibold text-red-800 mb-2">Rejection Details</h4>
                            <div className="text-sm text-red-700">
                              <p><strong>Reason:</strong> {submissionStatus.studio.rejectionReason}</p>
                              {submissionStatus.studio.adminNotes && (
                                <p className="mt-2"><strong>Admin Notes:</strong> {submissionStatus.studio.adminNotes}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center mb-6">
          <FaMicrophone className="text-purple-600 text-3xl mr-4" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Podcast Studio Registration</h1>
            <p className="text-gray-600">Register your studio to offer recording services to podcast creators</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className={`space-y-8 ${!canSubmit ? 'opacity-50 pointer-events-none' : ''}`}>
          {/* Studio Information */}
          <div className="bg-gray-50 p-6 rounded-lg">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <FaMicrophone className="mr-2 text-purple-600" />
              Studio Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Studio Name *
                </label>
                <input
                  type="text"
                  name="studioName"
                  value={formData.studioName}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Enter studio name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Capacity (Max People) *
                </label>
                <input
                  type="number"
                  name="capacity"
                  value={formData.capacity}
                  onChange={handleChange}
                  required
                  min="1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="e.g., 5"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Hourly Rate (₹) *
                </label>
                <input
                  type="number"
                  name="hourlyRate"
                  value={formData.hourlyRate}
                  onChange={handleChange}
                  required
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="e.g., 2000"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Location *
                </label>
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="e.g., Mumbai, Maharashtra"
                />
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-gray-50 p-6 rounded-lg">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <FaPhone className="mr-2 text-purple-600" />
              Contact Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contact Person *
                </label>
                <input
                  type="text"
                  name="contactPerson"
                  value={formData.contactPerson}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Full name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="10-digit phone number"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="studio@example.com"
                />
              </div>
            </div>
          </div>

          {/* Address */}
          <div className="bg-gray-50 p-6 rounded-lg">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <FaMapMarkerAlt className="mr-2 text-purple-600" />
              Address
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Address *
                </label>
                <textarea
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  required
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Enter complete address"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    State *
                  </label>
                  <select
                    name="state"
                    value={formData.state}
                    onChange={handleStateChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="">Select State</option>
                    {states.map((state) => (
                      <option key={state} value={state}>
                        {state}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    City *
                  </label>
                  <select
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    required
                    disabled={!formData.state}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <option value="">Select City</option>
                    {cities.map((city) => (
                      <option key={city} value={city}>
                        {city}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Pincode *
                  </label>
                  <input
                    type="text"
                    name="pincode"
                    value={formData.pincode}
                    onChange={handleChange}
                    required
                    pattern="[0-9]{6}"
                    maxLength="6"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="6-digit pincode"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Facilities */}
          <div className="bg-gray-50 p-6 rounded-lg">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Available Facilities</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {facilitiesOptions.map((facility) => {
                const IconComponent = facility.icon;
                return (
                  <label key={facility.id} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="facilities"
                      value={facility.id}
                      checked={formData.facilities.includes(facility.id)}
                      onChange={handleChange}
                      className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    />
                    <div className="flex items-center space-x-2">
                      {IconComponent && <IconComponent className="w-4 h-4 text-gray-600" />}
                      <span className="text-sm text-gray-700">{facility.label}</span>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Availability */}
          <div className="bg-gray-50 p-6 rounded-lg">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <FaClock className="mr-2 text-purple-600" />
              Availability
            </h2>
            <div className="space-y-4">
              {Object.entries(formData.availability).map(([day, schedule]) => (
                <div key={day} className="flex items-center space-x-4 p-3 bg-white rounded-lg">
                  <div className="w-20">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        name={`availability.${day}.available`}
                        checked={schedule.available}
                        onChange={handleChange}
                        className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      />
                      <span className="text-sm font-medium capitalize">{day}</span>
                    </label>
                  </div>
                  {schedule.available && (
                    <div className="flex items-center space-x-2">
                      <input
                        type="time"
                        name={`availability.${day}.start`}
                        value={schedule.start}
                        onChange={handleChange}
                        className="px-2 py-1 border border-gray-300 rounded text-sm"
                      />
                      <span className="text-gray-500">to</span>
                      <input
                        type="time"
                        name={`availability.${day}.end`}
                        value={schedule.end}
                        onChange={handleChange}
                        className="px-2 py-1 border border-gray-300 rounded text-sm"
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Studio Images */}
          <div className="bg-gray-50 p-6 rounded-lg">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <FaImage className="mr-2 text-purple-600" />
              Studio Images *
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              Upload photos of your studio (minimum 1 image required, max 10 images, 5MB each)
            </p>

            {/* Image Upload Area */}
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-purple-400 transition-colors">
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
                id="image-upload"
              />
              <label
                htmlFor="image-upload"
                className="cursor-pointer flex flex-col items-center"
              >
                <FaUpload className="text-4xl text-gray-400 mb-2" />
                <span className="text-lg font-medium text-gray-600 mb-1">
                  Click to upload images
                </span>
                <span className="text-sm text-gray-500">
                  PNG, JPG, JPEG up to 5MB each
                </span>
              </label>
            </div>

            {/* Image Previews */}
            {imagePreviews.length > 0 && (
              <div className="mt-6">
                <h3 className="text-lg font-medium text-gray-900 mb-3">
                  Selected Images ({imagePreviews.length}/10)
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {imagePreviews.map((preview, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={preview.preview}
                        alt={`Studio preview ${index + 1}`}
                        className="w-full h-32 object-cover rounded-lg border border-gray-200"
                      />
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <FaTimes className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Description */}
          <div className="bg-gray-50 p-6 rounded-lg">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Studio Description</h2>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="4"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              placeholder="Describe your studio, equipment, and what makes it special..."
            />
          </div>

          {/* Confirmation Checkbox */}
          <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
            <div className="flex items-start">
              <div className="flex items-center h-5">
                <input
                  id="confirm-details"
                  type="checkbox"
                  checked={confirmDetails}
                  onChange={(e) => setConfirmDetails(e.target.checked)}
                  className="w-4 h-4 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500 focus:ring-2"
                />
              </div>
              <div className="ml-3">
                <label htmlFor="confirm-details" className="text-sm font-medium text-gray-900">
                  I confirm that all the details provided above are accurate and complete
                </label>
                <p className="text-xs text-gray-600 mt-1">
                  Please verify all information before submitting. Incorrect details may result in rejection.
                </p>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isLoading || !canSubmit || !confirmDetails}
              className="bg-gradient-to-r from-purple-600 to-purple-700 text-white px-8 py-3 rounded-lg font-medium hover:from-purple-700 hover:to-purple-800 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? "Submitting..." : !canSubmit ? "Registration Not Available" : !confirmDetails ? "Please Confirm Details" : "Register Studio"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PodcastStudioRegister;
